<template>
    <div>
        <page-header>
            Brand Standards
            <template v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="sm"
                >
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        widthAuto
                        class="hidden! sm:inline-block!"
                        :icon="faCircleQuestion"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        widthAuto
                        class="inline-block! sm:hidden!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <message-standards-overview v-show="showOverview" class="mb-14"></message-standards-overview>

        <vx-alert v-if="list.length >= 20" :closeable="false" shadow class="mt-14 mb-8">
            Reached maximum of 20 brand standards
        </vx-alert>

        <message-standards-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            :list="list"
            :loading="loading"
        ></message-standards-controls>

        <page-loading v-if="loading && !list.length"></page-loading>
        <empty-state v-else-if="showEmptyState" @reset="resetFilters"></empty-state>
        <vx-table
            v-else
            :class="isAdminOrOwner ? 'xl:grid-cols-3-auto' : 'xl:grid-cols-2-auto'"
            rowBreakpoint="xl"
            class="mb-6"
        >
            <vx-table-row isHeader>
                <div>Name</div>
                <div>Targets</div>
            </vx-table-row>

            <message-standard-row
                v-if="showDefaultRow"
                data-test="default-row"
                :modelValue="{ name: 'Defaults', team_id: teamId }"
                :isDefault="true"
            ></message-standard-row>

            <message-standard-row
                v-for="item in list"
                :modelValue="item"
                :key="item.id"
                @deleted="removeFromList($event)"
            ></message-standard-row>
        </vx-table>

        <div v-if="list.length && fetchParams.cursor" class="mt-8">
            <vx-button
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { faCircleChevronDown, faCircleChevronUp, faCircleQuestion, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { computed, inject, onMounted, ref } from 'vue';
import type { Features } from '~/provides/features.types';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import MessageStandardsControls from './MessageStandardsControls.vue';
import MessageStandardsOverview from './MessageStandardsOverview.vue';
import MessageStandardRow from './MessageStandardRow.vue';
import { messageStandardsClient } from '~/services';
import type {
    GetMessageStandardsParams,
    MessageStandardType,
} from '~/services/messageStandards/messageStandardsClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useMessageStandardsGroupsCount } from '~/components/settings/teams/message-standards/list/useMessageStandardsGroupsCount';
import { isEqual } from '~/utils/helpers';
import { EmptyState } from '~/components';

const showOverview = ref(true);

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES') as Features;

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<
    MessageStandardType,
    GetMessageStandardsParams
>();
const { fetchMessageStandardsGroupsCount, groupCounts } = useMessageStandardsGroupsCount();

const defaultMessageStandard = ref<MessageStandardType | null>(null);

const showDefaultRow = computed(() => {
    const selectedAgentId = fetchParams.value.ai_message_vetting_agent_id;
    if (selectedAgentId === undefined || selectedAgentId === null) {
        return true;
    }

    if (null === defaultMessageStandard.value) {
        return true;
    }

    const marketingAgents = defaultMessageStandard.value.marketing_rules?.ai_message_vetting_agents ?? [];
    const transactionalAgents = defaultMessageStandard.value.transactional_rules?.ai_message_vetting_agents ?? [];
    const allAgents = [...marketingAgents, ...transactionalAgents];

    return allAgents.some((agent) => agent.id === selectedAgentId);
});

const showEmptyState = computed(() => {
    return !isEqual(defaultFilters(), fetchParams.value) && 0 === list.value.length && !showDefaultRow.value;
});

const defaultFilters = (): GetMessageStandardsParams => ({
    per_page: 10,
    cursor: null,
    query: '',
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

onMounted(async () => {
    resetFilters();

    if (FEATURES.contact_groups) {
        try {
            const response = await messageStandardsClient.getDefaultMessageStandard({ teamId });
            defaultMessageStandard.value = response.data;
        } catch {
            defaultMessageStandard.value = null;
        }
    }
});

const loadData = () => {
    const context = {
        teamId: teamId,
    };
    fetchRecords(messageStandardsClient.getMessageStandards(context, { ...fetchParams.value })).then((data) => {
        if (data?.length) {
            const messageStandardIds = data
                .map((standard) => standard.id!)
                .filter((id) => groupCounts.value[id] === undefined);

            if (messageStandardIds.length) {
                fetchMessageStandardsGroupsCount({ teamId }, messageStandardIds);
            }
        }
    });
};

const removeFromList = (itemId: number) => {
    list.value = list.value.filter((item) => item.id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};
</script>
